import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronLeft, Zap, Shield, Star, Crown, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../components/AuthProvider';
import { apiUrl } from '../../lib/api';

import { getStoredRef } from '../../lib/affiliateRef';
const VOID_BG = '#05060f';
const CARD_BG = '#0D0D1A';
const TEAL = '#2DC896';
const AMBER = '#EF9F27';
const ICE = '#E8F4FF';
const MUTED = '#8899AA';
const MONO = "'JetBrains Mono', 'Courier New', monospace";
const ORBITRON = "'Orbitron', monospace";

const tiers = [
{
  id: 'recruit',
  name: 'RECRUIT',
  price: 0,
  executions: '1 EXECUTION / MONTH',
  tagline: 'See how RecallCore works on one real task before upgrading.',
  goodFit: 'First-time users testing the platform.',
  valueBullets: ['test one workflow', 'review output quality', 'no card required'],
  cta: 'START FREE',
  ctaPath: '/user-registration',
  highlight: false,
  recommended: false,
  icon: Shield,
  accentColor: TEAL
},
{
  id: 'starter',
  name: 'STARTER',
  price: 29,
  executions: '20 EXECUTIONS / MONTH',
  tagline: 'For solo operators who need help moving recurring business tasks each week.',
  goodFit: 'Freelancers, solo founders, and lean operators.',
  valueBullets: ['support outreach, research, drafts and follow-up', 'reduce manual busywork', 'turn ideas into usable outputs faster'],
  cta: 'CHOOSE STARTER',
  highlight: false,
  recommended: false,
  icon: Zap,
  accentColor: TEAL
},
{
  id: 'commander',
  name: 'COMMANDER',
  price: 99,
  executions: '75 EXECUTIONS / MONTH',
  tagline: 'For founders managing multiple priorities across sales, research, and operations.',
  goodFit: 'Growing businesses that need stronger weekly momentum.',
  valueBullets: ['keep lead follow-up and execution moving', 'increase output without adding headcount', 'maintain consistency across the week'],
  cta: 'CHOOSE COMMANDER',
  highlight: true,
  recommended: true,
  icon: Target,
  accentColor: AMBER
},
{
  id: 'operator',
  name: 'OPERATOR',
  price: 249,
  executions: '200 EXECUTIONS / MONTH',
  tagline: 'For businesses running active workflows across teams, clients, or internal operations.',
  goodFit: 'Execution-heavy businesses, small teams, and agencies in motion.',
  valueBullets: ['support multiple live operations', 'reduce bottlenecks on larger output volumes', 'centralize operational output in one system'],
  cta: 'CHOOSE OPERATOR',
  highlight: false,
  recommended: false,
  icon: Star,
  accentColor: TEAL
},
{
  id: 'elite',
  name: 'ELITE',
  price: 499,
  executions: '400 EXECUTIONS / MONTH',
  tagline: 'For agencies and high-activity operators who need broader execution capacity across the business.',
  goodFit: 'Agencies, multi-client operators, and businesses scaling execution.',
  valueBullets: ['handle larger monthly workload', 'maintain speed across client and internal execution', 'scale output without expanding overhead too quickly'],
  cta: 'CHOOSE ELITE',
  highlight: false,
  recommended: false,
  icon: Crown,
  accentColor: TEAL
}];


const PricingSimplifiedExecutionPlans = () => {
  const navigate = useNavigate();
  const { user, session, isAuthenticated } = useAuth();
  const [loadingTier, setLoadingTier] = useState(null);
  const [error, setError] = useState(null);
  const [billing, setBilling] = useState('monthly');

  const annualPricing = {
    starter:   { price: '$299/yr',   note: 'Get 2 months free' },
    commander: { price: '$999/yr',   note: 'Get 2 months free' },
    operator:  { price: '$2,490/yr', note: 'Priority annual pricing' },
    elite:     { price: '$4,990/yr', note: 'Best annual rate' },
  };

  const handleTierClick = async (tier) => {
    if (tier?.id === 'recruit') {
      navigate('/user-registration');
      return;
    }

    setLoadingTier(tier?.id);
    setError(null);

    try {
      if (!session) {
        navigate('/user-login', {
          state: { from: `${window.location.pathname}${window.location.search}${window.location.hash}` },
        });
        return;
      }

      const jwt = session?.access_token;

      const res = await fetch(apiUrl('/api/checkout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`
        },
        body: JSON.stringify({ tier: tier?.id, ref: (new URLSearchParams(window.location.search).get('ref') || getStoredRef()) || undefined, interval: billing === 'annual' ? 'yearly' : 'monthly' })
      });

      if (!res?.ok) {
        throw new Error(`Checkout failed: please try again`);
      }

      const data = await res?.json();
      if (data && data?.url) {
        setLoadingTier(null);
        window.location.href = data?.url;
        return;
      } else {
        throw new Error('Checkout failed: please try again');
      }
    } catch (err) {
      setError(err?.message || 'Checkout failed: please try again');
      setLoadingTier(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: VOID_BG, color: ICE, fontFamily: MONO }}>
      <Helmet>
        <title>Pricing — RecallCore Execution Plans</title>
        <meta name="description" content="Choose your execution tier. RECRUIT: free. STARTER: $29/month. COMMANDER: $99/month. OPERATOR: $249/month. ELITE: $499/month." />
      </Helmet>
      {/* Nav */}
      <div style={{ borderBottom: `1px solid rgba(45,200,150,0.2)`, background: 'rgba(5,6,15,0.95)', backdropFilter: 'blur(12px)' }} className="px-4 sm:px-6 py-4 flex items-center justify-between gap-3 sticky top-0 z-50">
        <button
          onClick={() => navigate('/')}
          className="flex min-w-0 items-center gap-2"
          style={{ color: 'rgba(45,200,150,0.6)', fontFamily: MONO, fontSize: '0.75rem', letterSpacing: '0.1em', background: 'none', border: 'none', cursor: 'pointer' }}
          onMouseEnter={(e) => e.currentTarget.style.color = TEAL}
          onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(45,200,150,0.6)'}>
          <ChevronLeft className="w-4 h-4" />
          &lt; RECALLCORE™
        </button>
        <nav className="hidden md:flex items-center gap-6">
          {[
          { label: 'HOME', path: '/' },
          { label: 'COMMAND', path: '/command-center-ai-execution-interface' },
          { label: 'PRICING', path: '/pricing' }]?.
          map((item) =>
          <button key={item?.path} onClick={() => navigate(item?.path)}
          style={{ color: TEAL, fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer' }}
          onMouseEnter={(e) => e.currentTarget.style.color = ICE}
          onMouseLeave={(e) => e.currentTarget.style.color = TEAL}>
            {item?.label}</button>
          )}
        </nav>
        <button
          onClick={() => navigate('/command-center-ai-execution-interface')}
          className="shrink-0"
          style={{ background: AMBER, color: VOID_BG, padding: '8px clamp(10px, 2vw, 18px)', fontFamily: MONO, fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', border: 'none', cursor: 'pointer' }}>
          LAUNCH MISSION
        </button>
      </div>
      <div className="mx-auto max-w-[90rem] px-4 py-14 sm:px-6 sm:py-20">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-center mb-16">
          <h1 style={{ fontFamily: ORBITRON, fontWeight: 900, fontSize: 'clamp(2rem, 6vw, 3.5rem)', lineHeight: 1.1, marginBottom: '1rem', color: ICE }}>
            CHOOSE YOUR<br />
            <span style={{ color: TEAL }}>EXECUTION CAPACITY.</span>
          </h1>
          <p style={{ color: MUTED, fontSize: '0.9rem', fontFamily: MONO, maxWidth: '40rem', margin: '0 auto' }}>
            RecallCore turns instructions into finished business deliverables.<br />
            Choose the plan that matches how much work you need moved each month.
          </p>
        </motion.div>

        {/* Error */}
        {error &&
        <div style={{ background: 'rgba(239,159,39,0.1)', border: `1px solid ${AMBER}`, color: AMBER, padding: '12px 20px', fontFamily: MONO, fontSize: '0.8rem', marginBottom: '2rem', textAlign: 'center' }}>
            ⚠ {error}
          </div>
        }

        {/* Billing Toggle */}
        <div className="flex justify-center mb-10">
          <div style={{ display: 'inline-flex', background: 'rgba(13,13,26,0.9)', border: '1px solid rgba(45,200,150,0.25)', padding: '4px', gap: '4px' }}>
            <button
              onClick={() => setBilling('monthly')}
              style={{
                padding: '8px 24px',
                fontFamily: MONO,
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                border: 'none',
                cursor: 'pointer',
                background: billing === 'monthly' ? TEAL : 'transparent',
                color: billing === 'monthly' ? VOID_BG : MUTED,
                transition: 'background 0.2s, color 0.2s'
              }}>
              MONTHLY
            </button>
            <button
              onClick={() => setBilling('annual')}
              style={{
                padding: '8px 24px',
                fontFamily: MONO,
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                border: 'none',
                cursor: 'pointer',
                background: billing === 'annual' ? TEAL : 'transparent',
                color: billing === 'annual' ? VOID_BG : MUTED,
                transition: 'background 0.2s, color 0.2s'
              }}>
              ANNUAL
            </button>
          </div>
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 gap-5 mb-8 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
          {tiers?.map((tier, i) => {
            const TierIcon = tier?.icon;
            const isCommander = tier?.id === 'commander';
            const borderColor = isCommander ? AMBER : `rgba(45,200,150,0.25)`;
            const glowColor = isCommander ? `rgba(239,159,39,0.15)` : `rgba(45,200,150,0.08)`;
            const isLoading = loadingTier === tier?.id;
            const isRecruit = tier?.id === 'recruit';
            const annualData = annualPricing?.[tier?.id];
            const showAnnual = billing === 'annual' && !isRecruit && annualData;

            return (
              <motion.div
                key={tier?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                style={{
                  position: 'relative',
                  background: CARD_BG,
                  border: `${isCommander ? '2px' : '1px'} solid ${borderColor}`,
                  padding: '1.75rem 1.5rem',
                  boxShadow: isCommander ? `0 0 40px ${glowColor}, 0 0 80px rgba(239,159,39,0.05)` : `0 0 20px ${glowColor}`,
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  overflowWrap: 'anywhere'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = isCommander ? AMBER : TEAL;
                  e.currentTarget.style.boxShadow = isCommander ? `0 0 50px rgba(239,159,39,0.25)` : `0 0 30px rgba(45,200,150,0.15)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = borderColor;
                  e.currentTarget.style.boxShadow = isCommander ? `0 0 40px ${glowColor}, 0 0 80px rgba(239,159,39,0.05)` : `0 0 20px ${glowColor}`;
                }}>
                {/* Recommended badge */}
                <div style={{ minHeight: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', margin: '-0.75rem 0 0.5rem' }}>
                  {tier?.recommended &&
                  <div style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ background: AMBER, color: VOID_BG, fontSize: '0.6rem', fontWeight: 700, padding: '3px 14px', fontFamily: MONO, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                      ★ MOST POPULAR
                    </span>
                  </div>
                  }
                </div>
                {/* Icon + Name */}
                <div className="flex items-center gap-2 mb-4">
                  <TierIcon className="w-4 h-4 flex-shrink-0" style={{ color: tier?.accentColor }} />
                  <h3 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '0.95rem', color: tier?.accentColor, letterSpacing: '0.1em' }}>{tier?.name}</h3>
                </div>
                {/* Price */}
                <div className="mb-1 min-w-0">
                  {tier?.price === 0 ?
                  <span style={{ fontFamily: ORBITRON, fontWeight: 900, fontSize: '2rem', color: ICE }}>FREE</span> :
                  showAnnual ?
                  <span style={{ fontFamily: ORBITRON, fontWeight: 900, fontSize: 'clamp(1.35rem, 2vw, 1.7rem)', color: ICE, whiteSpace: 'nowrap' }}>{annualData?.price}</span> :
                  <>
                      <span style={{ fontFamily: ORBITRON, fontWeight: 900, fontSize: '2rem', color: ICE }}>${tier?.price}</span>
                      <span style={{ color: MUTED, fontSize: '0.75rem', fontFamily: MONO }}>/month</span>
                    </>
                  }
                </div>
                {/* Annual savings note */}
                {showAnnual &&
                  <div style={{ color: TEAL, fontFamily: MONO, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                    {annualData?.note}
                  </div>
                }
                {/* Executions */}
                <div style={{ color: tier?.accentColor, fontFamily: MONO, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', marginBottom: '1rem', textTransform: 'uppercase' }}>
                  {tier?.executions}
                </div>
                {/* Tagline / Body */}
                <p style={{ color: ICE, fontSize: '0.73rem', fontFamily: MONO, lineHeight: 1.65, marginBottom: '0.75rem' }}>{tier?.tagline}</p>
                {/* Good fit for */}
                <div style={{ marginBottom: '0.85rem' }}>
                  <span style={{ color: MUTED, fontSize: '0.65rem', fontFamily: MONO, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '0.25rem' }}>Good fit for:</span>
                  <span style={{ color: MUTED, fontSize: '0.71rem', fontFamily: MONO, lineHeight: 1.5 }}>{tier?.goodFit}</span>
                </div>
                {/* Value bullets */}
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem 0', flexGrow: 1 }}>
                  {tier?.valueBullets?.map((bullet, bi) => (
                    <li key={bi} style={{ color: MUTED, fontSize: '0.71rem', fontFamily: MONO, lineHeight: 1.6, display: 'flex', alignItems: 'flex-start', gap: '0.4rem' }}>
                      <span style={{ color: tier?.accentColor, flexShrink: 0 }}>—</span>
                      {bullet}
                    </li>
                  ))}
                </ul>
                {/* CTA Button */}
                <button
                  onClick={() => handleTierClick(tier)}
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    padding: '11px 12px',
                    fontFamily: MONO,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    background: isCommander ? AMBER : tier?.id === 'recruit' ? TEAL : 'transparent',
                    color: isCommander || tier?.id === 'recruit' ? VOID_BG : tier?.accentColor,
                    border: isCommander || tier?.id === 'recruit' ? 'none' : `1px solid ${tier?.accentColor}`,
                    opacity: isLoading ? 0.6 : 1,
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => {if (!isLoading) e.currentTarget.style.opacity = '0.85';}}
                  onMouseLeave={(e) => {e.currentTarget.style.opacity = isLoading ? '0.6' : '1';}}>
                  {isLoading ? 'PROCESSING...' : `[ ${tier?.cta} ]`}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Footer note */}
        <p style={{ textAlign: 'center', color: MUTED, fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.05em', marginBottom: '1rem', lineHeight: 1.7 }}>
          1 mission = one complete finished business deliverable.<br />
          Research. Strategy. Plan. System. Execution. Delivered.
        </p>

        {/* Early Adopter Notice */}
        <div className="flex justify-center mb-12">
          <p style={{ fontFamily: MONO, fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', textAlign: 'center', fontStyle: 'italic', letterSpacing: '0.05em', lineHeight: '1.6', maxWidth: '520px' }}>
            Early adopter pricing is available for the first 500 accounts. After that, pricing for new customers will increase.
          </p>
        </div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mt-12">
          <h2 style={{ fontFamily: ORBITRON, fontWeight: 900, fontSize: '1.8rem', color: ICE, marginBottom: '1rem' }}>READY TO EXECUTE?</h2>
          <p style={{ color: MUTED, fontFamily: MONO, fontSize: '0.8rem', marginBottom: '2rem' }}>Start free. Upgrade when you need more capacity.</p>
          <button
            onClick={() => navigate('/command-center-ai-execution-interface')}
            style={{ background: AMBER, color: VOID_BG, padding: '14px 40px', fontFamily: MONO, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            LAUNCH MISSION <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
      {/* Footer */}
      <footer className="py-8 px-6 text-center" style={{ borderTop: `1px solid rgba(45,200,150,0.15)`, background: VOID_BG }}>
        <p style={{ color: 'rgba(45,200,150,0.6)', fontSize: '0.7rem', fontFamily: MONO }}>© 2026 RecallCore. All rights reserved.</p>
        <div className="flex justify-center gap-6 mt-3">
          {[['Privacy', '/privacy'], ['Terms', '/terms'], ['Contact', '/contact']].map(([link, href]) => <a key={link} href={href} style={{ color: 'rgba(45,200,150,0.4)', fontSize: '0.65rem', fontFamily: MONO, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = TEAL} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(45,200,150,0.4)'}>{link}</a>)}
        </div>
      </footer>
    </div>
  );
};

export default PricingSimplifiedExecutionPlans;
